/**
 * Single source of truth for CRM metadata: name, logo, optional bgColor, aliases.
 *
 * Used by:
 * - useCrmStatus / useRollout hooks
 * - Connect/disconnect UI
 * - Contact list rendering
 *
 * Note: convex/rollout/mutations.ts has its own copy of name lookups since
 * Convex functions can only import from convex/. Keep them in sync manually.
 */

export interface CrmConfig {
  /** Primary appKey used by Rollout / our internal storage */
  slug: string;
  /** Friendly display name */
  name: string;
  /** Hosted logo URL (optional — falls back to icon/bgColor combo) */
  logoUrl?: string;
  /** Tailwind background color class for placeholder when no logo */
  bgColor?: string;
  /** Single-letter or short icon used inside the bgColor placeholder */
  icon?: string;
  /** Alternative slugs that should resolve to this same entry */
  aliases?: string[];
}

export const crmConfigs: CrmConfig[] = [
  {
    slug: "follow-up-boss",
    name: "Follow Up Boss",
    logoUrl: "/images/crms/fub-logo.png",
    aliases: ["fub", "followupboss"],
  },
  {
    slug: "boldtrail",
    name: "BoldTrail",
    bgColor: "bg-zinc-200",
    logoUrl: "https://imagedelivery.net/59SMqiAtGadWwwQ0wPknmg/1a97f2bf-c436-4cf8-bfd2-7ffe269fd000/public",
  },
  {
    slug: "boldtrail-backoffice",
    name: "BoldTrail BackOffice",
  },
  {
    slug: "brivity",
    name: "Brivity",
    bgColor: "bg-emerald-500",
    logoUrl: "https://imagedelivery.net/59SMqiAtGadWwwQ0wPknmg/a1b6f7e0-a9ae-4cc5-a97c-0e0279003500/public",
  },
  {
    slug: "cinc",
    name: "CINC",
    bgColor: "bg-indigo-500",
    logoUrl: "https://imagedelivery.net/59SMqiAtGadWwwQ0wPknmg/c9ca9f70-0c50-4165-c17c-0ab93695c400/public",
  },
  {
    slug: "cloze",
    name: "Cloze",
    logoUrl: "https://imagedelivery.net/59SMqiAtGadWwwQ0wPknmg/041f9b5e-718b-4756-8b75-f669716f7500/public",
  },
  {
    slug: "cloze-api-key",
    name: "Cloze - Api Key",
    logoUrl: "https://imagedelivery.net/59SMqiAtGadWwwQ0wPknmg/041f9b5e-718b-4756-8b75-f669716f7500/public",
  },
  {
    slug: "constant-contact",
    name: "Constant Contact",
    logoUrl: "https://imagedelivery.net/59SMqiAtGadWwwQ0wPknmg/28da44ec-1d2f-4554-f548-db059bba5700/public",
  },
  {
    slug: "docusign",
    name: "Docusign",
  },
  {
    slug: "dotloop",
    name: "Dotloop",
  },
  {
    slug: "dotloop-legacy",
    name: "Dotloop Legacy",
  },
  {
    slug: "encompass",
    name: "Encompass",
  },
  {
    slug: "gmail",
    name: "Gmail",
  },
  {
    slug: "gmail-otp",
    name: "Gmail for OTP",
  },
  {
    slug: "highlevel",
    name: "HighLevel",
    bgColor: "bg-blue-600",
    logoUrl: "https://imagedelivery.net/59SMqiAtGadWwwQ0wPknmg/42b3b49b-76c1-47a6-603d-35f54c3d3300/public",
  },
  {
    slug: "hubspot",
    name: "HubSpot",
    bgColor: "bg-orange-600",
    logoUrl: "https://imagedelivery.net/59SMqiAtGadWwwQ0wPknmg/645f1f80-4d02-45a1-5aca-2951dcfa8600/public",
  },
  {
    slug: "kvcore",
    name: "kvCORE",
    bgColor: "bg-purple-500",
  },
  {
    slug: "kw-command",
    name: "KW Command",
    logoUrl: "https://imagedelivery.net/59SMqiAtGadWwwQ0wPknmg/14515803-cbd9-4f59-babe-e8bef582fa00/public",
  },
  {
    slug: "liondesk",
    name: "LionDesk",
    bgColor: "bg-orange-500",
    logoUrl: "https://imagedelivery.net/59SMqiAtGadWwwQ0wPknmg/8d9307fb-a497-4d72-1467-9dcc2fe98600/public",
  },
  {
    slug: "lofty",
    name: "Lofty (Chime)",
    bgColor: "bg-blue-500",
    logoUrl: "https://imagedelivery.net/59SMqiAtGadWwwQ0wPknmg/73f077fd-0079-41d8-ff9a-4c8480fe1300/public",
  },
  {
    slug: "open-to-close",
    name: "Open to Close",
  },
  {
    slug: "real-geeks",
    name: "Real Geeks",
    bgColor: "bg-red-500",
    logoUrl: "https://imagedelivery.net/59SMqiAtGadWwwQ0wPknmg/a800d557-9d59-43a7-7ee6-a01711a9f200/public",
  },
  {
    slug: "rezen",
    name: "ReZEN",
  },
  {
    slug: "sierra-interactive",
    name: "Sierra Interactive",
    bgColor: "bg-teal-500",
    logoUrl: "https://imagedelivery.net/59SMqiAtGadWwwQ0wPknmg/71ee29b3-addd-4d1e-429e-1f25a1120e00/public",
  },
  {
    slug: "sisu",
    name: "Sisu",
  },
  {
    slug: "skyslope",
    name: "Skyslope",
  },
  {
    slug: "sure-send",
    name: "Sure Send",
    logoUrl: "https://imagedelivery.net/59SMqiAtGadWwwQ0wPknmg/ffced902-0339-4eca-bb3a-2a2a7c827f00/public",
  },
  {
    slug: "wise-agent",
    name: "Wise Agent",
    bgColor: "bg-yellow-500",
    logoUrl: "https://imagedelivery.net/59SMqiAtGadWwwQ0wPknmg/e49a9ca2-7854-452b-1eca-5221b86bc100/public",
  },
];

const crmConfigBySlug = (() => {
  const map = new Map<string, CrmConfig>();
  for (const config of crmConfigs) {
    map.set(config.slug, config);
    for (const alias of config.aliases ?? []) {
      map.set(alias, config);
    }
  }
  return map;
})();

const localCrmLogoSlugs = new Set([
  "boldtrail",
  "boldtrail-backoffice",
  "brivity",
  "cinc",
  "cloze",
  "docusign",
  "dotloop",
  "dotloop-legacy",
  "encompass",
  "gmail",
  "gmail-otp",
  "highlevel",
  "hubspot",
  "kw-command",
  "liondesk",
  "lofty",
  "open-to-close",
  "real-geeks",
  "rezen",
  "sierra-interactive",
  "sisu",
  "skyslope",
  "sure-send",
  "wise-agent",
]);

/** Look up the full CRM config (name + logo + bgColor) by slug or alias. */
export function getCrmConfig(slug: string | null | undefined): CrmConfig | undefined {
  if (!slug) return undefined;
  return crmConfigBySlug.get(slug);
}

/** Friendly display name. Returns the slug as-is if no entry matches. */
export function getCrmName(slug: string | null | undefined): string {
  if (!slug) return "CRM";
  return crmConfigBySlug.get(slug)?.name ?? slug;
}
/** Configured or known local logo URL, if one exists for this slug or alias. */
export function getCrmLogoUrl(slug: string | null | undefined): string | undefined {
  const config = getCrmConfig(slug);
  if (!config) return undefined;
  if (config.logoUrl) return config.logoUrl;
  return localCrmLogoSlugs.has(config.slug) ? `/images/crms/${config.slug}.svg` : undefined;
}

/**
 * Slugs of CRMs our platform actively supports. Used to filter connector
 * grids so we don't surface CRMs we can't ingest from (e.g. Rollout lists
 * more than this upstream).
 */
export const supportedCrmSlugs: string[] = [
  "follow-up-boss",
  "boldtrail",
  "brivity",
  "cinc",
  "encompass",
  "highlevel",
  "kw-command",
  "liondesk",
  "lofty",
  "open-to-close",
  "real-geeks",
  "rezen",
  "sierra-interactive",
  "sisu",
  "skyslope",
  "sure-send",
  "wise-agent",
];

/** Shown first by default in connect UIs; the rest go behind "View all CRMs". */
export const primaryCrmSlugs: string[] = ["follow-up-boss", "lofty", "boldtrail"];

/** Supported CRMs that are NOT primary. */
export const secondaryCrmSlugs: string[] = supportedCrmSlugs.filter(
  (slug) => !primaryCrmSlugs.includes(slug),
);
